import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Label } from '@/components/ui/shadcn/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group';
import { useControllableOpen } from '@/hooks/use-controllable-open';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { router } from '@inertiajs/react';
import axios from 'axios';
import { AlertTriangle, Building2, Loader2, Plus, X } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { route } from 'ziggy-js';

export interface MergeCompany {
    id: string;
    name: string;
    company_code?: string | null;
    country?: string | null;
}

interface Suggestion extends MergeCompany {
    score: number;
    conflict: string | null;
}

interface RegistryCorrection {
    uuid: string;
    registry_name: string;
    registry_code: string;
    current_name: string;
    current_code: string;
    changes_name: boolean;
    changes_code: boolean;
}

interface PreviewResponse {
    totals: Record<string, number>;
    per_company: Record<string, number>;
    conflict: string | null;
    registry_candidate: RegistryCorrection | null;
}

interface MergeCompaniesModalProps {
    /** Companies already in the selection — one from a company page, many from the index. */
    companies: MergeCompany[];
    /** Company to look for duplicates of. Defaults to the first selected company. */
    suggestionsFor?: string;
    trigger?: ReactNode;
    /** Set to drive the modal from the page — see useControllableOpen. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

/** Above this the match is near-certain — an identical valid registration code alone scores 70. */
const HIGH_CONFIDENCE_SCORE = 70;

const withCount = (template: string, count: number): string => template.replace(':count', String(count));

export default function MergeCompaniesModal({ companies, suggestionsFor, trigger, open: openProp, onOpenChange }: MergeCompaniesModalProps) {
    const { t } = useTranslations();
    const [open, setOpen] = useControllableOpen(openProp, onOpenChange);

    const [step, setStep] = useState<'select' | 'survivor'>('select');
    const [selected, setSelected] = useState<MergeCompany[]>(companies);
    const [survivorId, setSurvivorId] = useState<string>(companies[0]?.id ?? '');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState(false);
    const [preview, setPreview] = useState<PreviewResponse | null>(null);
    const [applyRegistry, setApplyRegistry] = useState(false);
    const [processing, setProcessing] = useState(false);

    const [searchOpen, setSearchOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<MergeCompany[]>([]);
    const [searching, setSearching] = useState(false);
    const debouncedSearch = useDebounce(searchTerm, 300);

    const selectedIds = useMemo(() => selected.map((c) => c.id), [selected]);
    const selectedKey = useMemo(() => [...selectedIds].sort().join(','), [selectedIds]);
    const sourceCompanyId = suggestionsFor ?? companies[0]?.id;

    const conflictLabel = useCallback(
        (reason: string | null): string | null => (reason ? t(`companies.merge.conflict_${reason}`, '') || null : null),
        [t],
    );

    const reset = useCallback(() => {
        setStep('select');
        setSelected(companies);
        setSurvivorId(companies[0]?.id ?? '');
        setSuggestions([]);
        setPreview(null);
        setApplyRegistry(false);
        setSearchTerm('');
        setSearchResults([]);
        setSearchOpen(false);
    }, [companies]);

    // Seed the selection each time the sheet opens: the component stays mounted
    // while the page's selection changes underneath it.
    useEffect(() => {
        if (!open) return;
        setSelected(companies);
        setSurvivorId(companies[0]?.id ?? '');
        // Re-seeding on every `companies` identity change would fight the user's edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Suggested duplicates for the company the merge was started from.
    useEffect(() => {
        if (!open || !sourceCompanyId) return;

        let cancelled = false;
        setLoadingSuggestions(true);

        void (async () => {
            try {
                const res = await axios.get(route('companies.merge.suggestions', { company: sourceCompanyId }));
                if (!cancelled) setSuggestions(res.data?.candidates ?? []);
            } catch {
                if (!cancelled) toast.error(t('companies.merge.errors.load_failed', 'Could not load duplicate suggestions.'));
            } finally {
                if (!cancelled) setLoadingSuggestions(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, sourceCompanyId, t]);

    // Record counts and identity conflicts for whatever is currently selected.
    useEffect(() => {
        if (!open || selectedIds.length === 0) {
            setPreview(null);
            return;
        }

        let cancelled = false;

        void (async () => {
            try {
                const res = await axios.post(route('companies.merge.preview'), {
                    survivor_uuid: survivorId || undefined,
                    company_uuids: selectedIds,
                });
                if (cancelled) return;
                setPreview(res.data);
                // Changing the survivor changes which registry entry is on offer,
                // so the opt-in must not carry over from the previous one.
                setApplyRegistry(false);
            } catch {
                if (!cancelled) setPreview(null);
            }
        })();

        return () => {
            cancelled = true;
        };
        // selectedKey stands in for selectedIds so a re-render with the same set doesn't refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, selectedKey, survivorId]);

    // Free-text company search for adding anything the suggestions missed.
    useEffect(() => {
        if (!searchOpen) return;

        let cancelled = false;
        setSearching(true);

        void (async () => {
            try {
                const res = await axios.get('/api/companies', {
                    params: { search: debouncedSearch.trim() || undefined, limit: 20, page: 1 },
                });
                if (cancelled) return;
                setSearchResults(
                    ((res.data?.companies ?? []) as Array<{ uuid: string; name: string; company_code?: string; country?: string }>).map((c) => ({
                        id: c.uuid,
                        name: c.name,
                        company_code: c.company_code ?? null,
                        country: c.country ?? null,
                    })),
                );
            } catch {
                if (!cancelled) setSearchResults([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [searchOpen, debouncedSearch]);

    const toggle = (company: MergeCompany, checked: boolean) => {
        setSelected((prev) => {
            if (!checked) {
                const next = prev.filter((c) => c.id !== company.id);
                // The survivor can't be a company that is no longer in the merge.
                if (survivorId === company.id) setSurvivorId(next[0]?.id ?? '');
                return next;
            }
            return prev.some((c) => c.id === company.id) ? prev : [...prev, company];
        });
    };

    const handleOpenChange = (next: boolean) => {
        if (!next) reset();
        setOpen(next);
    };

    const handleSubmit = () => {
        setProcessing(true);

        router.post(
            route('companies.merge'),
            {
                survivor_uuid: survivorId,
                company_uuids: selectedIds,
                registry_candidate_uuid: applyRegistry ? (preview?.registry_candidate?.uuid ?? null) : null,
            },
            {
                onSuccess: () => {
                    setOpen(false);
                    reset();
                },
                onError: (errors) => {
                    toast.error(Object.values(errors)[0] ?? t('common.error', 'Something went wrong.'));
                },
                onFinish: () => setProcessing(false),
            },
        );
    };

    const mergeAwayCount = Math.max(0, selected.length - 1);
    const conflict = conflictLabel(preview?.conflict ?? null);
    const movingTotal = Object.values(preview?.totals ?? {}).reduce((sum, n) => sum + n, 0);

    const recordsLabel = (companyId: string): string => {
        const count = preview?.per_company?.[companyId];
        if (count === undefined) return '';
        return count === 0 ? t('companies.merge.no_records', 'No linked records') : withCount(t('companies.merge.records', ':count records'), count);
    };

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            {trigger ? <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger> : null}
            <ResponsiveModalContent size="md">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>{t('companies.merge.title', 'Merge duplicate companies')}</ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {step === 'select'
                            ? t('companies.merge.description', 'Move everything from duplicate companies onto one surviving record.')
                            : t(
                                  'companies.merge.keep_hint',
                                  'The company you keep holds on to its own details. Blank fields are filled in from the others.',
                              )}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="space-y-5">
                    {step === 'select' ? (
                        <>
                            <div className="space-y-1.5">
                                <Label>{t('companies.merge.suggested', 'Suggested matches')}</Label>

                                {loadingSuggestions ? (
                                    <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    </div>
                                ) : suggestions.length === 0 ? (
                                    <p className="text-muted-foreground text-sm">
                                        {t(
                                            'companies.merge.suggested_empty',
                                            'No likely duplicates found. You can still search for a company below.',
                                        )}
                                    </p>
                                ) : (
                                    <div className="rounded-md border">
                                        {suggestions.map((suggestion, i) => {
                                            const isSelected = selectedIds.includes(suggestion.id);
                                            const warning = conflictLabel(suggestion.conflict);

                                            return (
                                                <div
                                                    key={suggestion.id}
                                                    className={cn('flex items-start gap-2.5 px-3 py-2.5', i < suggestions.length - 1 && 'border-b')}
                                                >
                                                    <Checkbox
                                                        id={`merge-${suggestion.id}`}
                                                        className="mt-0.5"
                                                        checked={isSelected}
                                                        onCheckedChange={(val) => toggle(suggestion, !!val)}
                                                    />
                                                    <label htmlFor={`merge-${suggestion.id}`} className="min-w-0 flex-1 cursor-pointer select-none">
                                                        <span className="flex flex-wrap items-center gap-1.5">
                                                            <span className="text-sm font-medium">{suggestion.name}</span>
                                                            {suggestion.company_code ? (
                                                                <Badge variant="outline" className="text-xs">
                                                                    {suggestion.company_code}
                                                                </Badge>
                                                            ) : null}
                                                            <Badge
                                                                variant="secondary"
                                                                className={cn(
                                                                    'text-xs',
                                                                    suggestion.score >= HIGH_CONFIDENCE_SCORE
                                                                        ? 'bg-success-subtle text-success-subtle-foreground'
                                                                        : 'bg-info-subtle text-info-subtle-foreground',
                                                                )}
                                                            >
                                                                {suggestion.score >= HIGH_CONFIDENCE_SCORE
                                                                    ? t('companies.merge.confidence_high', 'High confidence')
                                                                    : t('companies.merge.confidence_medium', 'Possible match')}
                                                            </Badge>
                                                        </span>
                                                        {warning ? (
                                                            <span className="text-warning-subtle-foreground mt-1 flex items-start gap-1.5 text-xs">
                                                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                                                {warning}
                                                            </span>
                                                        ) : null}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-1.5">
                                <Label>{t('companies.merge.search_label', 'Add another company')}</Label>
                                {/* modal: the sheet makes the rest of the page inert, which would
                                    otherwise swallow clicks on the popover list. */}
                                <Popover modal open={searchOpen} onOpenChange={setSearchOpen}>
                                    <PopoverTrigger asChild>
                                        <Button type="button" variant="outline" className="text-muted-foreground w-full justify-start font-normal">
                                            <Plus className="mr-2 h-4 w-4 shrink-0" />
                                            {t('companies.merge.search_placeholder', 'Search companies…')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                        <Command shouldFilter={false}>
                                            <CommandInput
                                                placeholder={t('companies.merge.search_placeholder', 'Search companies…')}
                                                value={searchTerm}
                                                onValueChange={setSearchTerm}
                                            />
                                            <CommandList className="max-h-64">
                                                {searching ? (
                                                    <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <CommandEmpty>{t('common.no_results', 'No results found.')}</CommandEmpty>
                                                        <CommandGroup>
                                                            {searchResults
                                                                .filter((c) => !selectedIds.includes(c.id))
                                                                .map((company) => (
                                                                    <CommandItem
                                                                        key={company.id}
                                                                        value={company.id}
                                                                        onSelect={() => {
                                                                            toggle(company, true);
                                                                            setSearchOpen(false);
                                                                        }}
                                                                        className="cursor-pointer"
                                                                    >
                                                                        <Building2 className="text-muted-foreground mr-2 h-4 w-4 shrink-0" />
                                                                        <span className="min-w-0 truncate">{company.name}</span>
                                                                        {company.company_code ? (
                                                                            <Badge variant="outline" className="ml-auto shrink-0 text-xs">
                                                                                {company.company_code}
                                                                            </Badge>
                                                                        ) : null}
                                                                    </CommandItem>
                                                                ))}
                                                        </CommandGroup>
                                                    </>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>

                            <div className="space-y-1.5">
                                <Label>{withCount(t('companies.merge.selected_count', ':count selected'), selected.length)}</Label>
                                <div className="rounded-md border">
                                    {selected.map((company, i) => (
                                        <div
                                            key={company.id}
                                            className={cn('flex items-center gap-2 px-3 py-2.5', i < selected.length - 1 && 'border-b')}
                                        >
                                            <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
                                            <span className="min-w-0 flex-1 truncate text-sm">{company.name}</span>
                                            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{recordsLabel(company.id)}</span>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 shrink-0"
                                                aria-label={t('companies.merge.cancel', 'Cancel')}
                                                onClick={() => toggle(company, false)}
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-1.5">
                                <Label>{t('companies.merge.keep_label', 'Everything will be merged into')}</Label>
                                <RadioGroup value={survivorId} onValueChange={setSurvivorId} className="gap-0 rounded-md border">
                                    {selected.map((company, i) => (
                                        <div
                                            key={company.id}
                                            className={cn('flex items-center gap-2.5 px-3 py-2.5', i < selected.length - 1 && 'border-b')}
                                        >
                                            <RadioGroupItem value={company.id} id={`survivor-${company.id}`} />
                                            <label htmlFor={`survivor-${company.id}`} className="min-w-0 flex-1 cursor-pointer select-none">
                                                <span className="flex flex-wrap items-center gap-1.5">
                                                    <span className="text-sm font-medium">{company.name}</span>
                                                    {company.company_code ? (
                                                        <Badge variant="outline" className="text-xs">
                                                            {company.company_code}
                                                        </Badge>
                                                    ) : null}
                                                </span>
                                            </label>
                                            <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{recordsLabel(company.id)}</span>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>

                            {preview?.registry_candidate ? (
                                <div className="space-y-1.5">
                                    <Label>{t('companies.merge.registry_label', 'Official registry entry')}</Label>
                                    <div className="space-y-2 rounded-md border px-3 py-2.5">
                                        <div className="flex items-start gap-2.5">
                                            <Checkbox
                                                id="merge-apply-registry"
                                                className="mt-0.5"
                                                checked={applyRegistry}
                                                onCheckedChange={(val) => setApplyRegistry(!!val)}
                                            />
                                            <label htmlFor="merge-apply-registry" className="cursor-pointer text-sm select-none">
                                                {t(
                                                    'companies.merge.registry_apply',
                                                    "Also correct the surviving company's name and code from the registry",
                                                )}
                                            </label>
                                        </div>
                                        <div className="text-muted-foreground space-y-0.5 pl-7 text-xs">
                                            {preview.registry_candidate.changes_name ? (
                                                <p>
                                                    <span className="line-through">{preview.registry_candidate.current_name}</span>
                                                    {' → '}
                                                    <span className="text-foreground font-medium">{preview.registry_candidate.registry_name}</span>
                                                </p>
                                            ) : null}
                                            {preview.registry_candidate.changes_code ? (
                                                <p>
                                                    <span className="line-through">{preview.registry_candidate.current_code}</span>
                                                    {' → '}
                                                    <span className="text-foreground font-medium">{preview.registry_candidate.registry_code}</span>
                                                </p>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {movingTotal > 0 ? (
                                <div className="space-y-1.5">
                                    <Label>{t('companies.merge.moving', 'Records moving to the surviving company')}</Label>
                                    <div className="rounded-md border">
                                        {Object.entries(preview?.totals ?? {})
                                            .filter(([, count]) => count > 0)
                                            .map(([key, count], i, entries) => (
                                                <div
                                                    key={key}
                                                    className={cn(
                                                        'flex items-center justify-between px-3 py-2 text-sm',
                                                        i < entries.length - 1 && 'border-b',
                                                    )}
                                                >
                                                    <span>{t(`companies.merge.relations.${key}`, key.replace(/_/g, ' '))}</span>
                                                    <span className="font-medium tabular-nums">{count}</span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ) : null}

                            {conflict ? (
                                <div className="bg-warning-subtle text-warning-subtle-foreground flex gap-2 rounded-md px-3 py-2.5 text-sm">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <span>{conflict}</span>
                                </div>
                            ) : null}

                            <div className="bg-muted text-muted-foreground flex gap-2 rounded-md px-3 py-2.5 text-sm">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>
                                    {t(
                                        'companies.merge.warning',
                                        'The companies you do not keep will be archived and every linked record moved to the surviving company.',
                                    )}
                                </span>
                            </div>
                        </>
                    )}
                </ResponsiveModalBody>

                <ResponsiveModalFooter>
                    <Button
                        variant="outline"
                        onClick={() => (step === 'survivor' ? setStep('select') : handleOpenChange(false))}
                        disabled={processing}
                    >
                        {step === 'survivor' ? t('common.back', 'Back') : t('companies.merge.cancel', 'Cancel')}
                    </Button>
                    {step === 'select' ? (
                        <Button onClick={() => setStep('survivor')} disabled={selected.length < 2}>
                            {t('companies.merge.step_survivor', 'Choose which company to keep')}
                        </Button>
                    ) : (
                        <Button onClick={handleSubmit} disabled={processing || !survivorId || selected.length < 2}>
                            {processing
                                ? t('common.saving', 'Saving...')
                                : mergeAwayCount === 1
                                  ? t('companies.merge.confirm_one', 'Merge 1 company')
                                  : withCount(t('companies.merge.confirm', 'Merge :count companies'), mergeAwayCount)}
                        </Button>
                    )}
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
